import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { useSupabase } from './SupabaseContext';
import { settingsService } from '@/app/services/settingsService';
import { featureFlagsService } from '@/app/services/featureFlagsService';
import { branchService } from '@/app/services/branchService';
import { permissionService } from '@/app/services/permissionService';
import type { EngineRole } from '@/app/services/permissionService';
import { permissionEngine } from '@/app/services/permissionEngine';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { isDebugErpEnabled } from '@/app/lib/debugErp';
import type { ModuleToggles } from '@/app/config/companyBootstrapRegistry';
import { defaultModuleToggles } from '@/app/config/companyBootstrapRegistry';
import { businessSettingsService, type BusinessSettings } from '@/app/services/businessSettingsService';
import { DEFAULT_BESPOKE_FORM_CONFIG, type BespokeFormConfig } from '@/app/lib/bespokeDefaults';
import { buildModuleTogglesFromConfigRows, moduleTogglePatchesToDb } from '@/app/config/moduleConfigSemantics';
import {
  mapAppRoleToEngineRole,
  mapAppRoleToUiRole,
  isPlatformOperatorAppRole,
} from '@/app/config/functionalRoles';
import { FISCAL_YEAR_CONFIG_UPDATED_EVENT } from '@/app/utils/financialYear';

export type { ModuleToggles };

// ============================================
// 🎯 TYPES & INTERFACES
// ============================================

export interface PaymentMethodConfig {
  id: string;
  method: string;
  enabled: boolean;
  defaultAccount: string;
}

export interface DefaultAccounts {
  paymentMethods: PaymentMethodConfig[];
}

/** LOCKED: Each module has its own prefix and counter. Do not share counters. */
export interface NumberingRules {
  salePrefix: string;           // SL – Regular Sale
  saleNextNumber: number;
  quotationPrefix?: string;
  quotationNextNumber?: number;
  draftPrefix?: string;
  draftNextNumber?: number;
  orderPrefix?: string;
  orderNextNumber?: number;
  purchasePrefix: string;       // PUR – Purchase
  purchaseNextNumber: number;
  rentalPrefix: string;
  rentalNextNumber: number;
  expensePrefix: string;        // EXP – Expense
  expenseNextNumber: number;
  productPrefix: string;
  productNextNumber: number;
  studioPrefix: string;         // STD – Studio Sale
  studioNextNumber: number;
  posPrefix: string;
  posNextNumber: number;
  productionPrefix?: string;
  productionNextNumber?: number;
  /** PAY – Payment (customer/supplier/worker) */
  paymentPrefix?: string;
  paymentNextNumber?: number;
  /** JOB – Worker job (studio stage payable) */
  jobPrefix?: string;
  jobNextNumber?: number;
  /** JV – Journal voucher */
  journalPrefix?: string;
  journalNextNumber?: number;
}

export interface UserPermissions {
  role: 'Admin' | 'Manager' | 'Staff';
  canCreateSale: boolean;
  canEditSale: boolean;
  canDeleteSale: boolean;
  /** Cancel invoice: Admin + Manager only (Cashier cannot) */
  canCancelSale?: boolean;
  canViewReports: boolean;
  canManageSettings: boolean;
  canManageUsers: boolean;
  canAccessAccounting: boolean;
  canMakePayments: boolean;
  canReceivePayments: boolean;
  canManageExpenses: boolean;
  canManageProducts: boolean;
  canManagePurchases: boolean;
  canManageRentals: boolean;
  /** Purchase delete restricted - default false for non-Admin */
  canEditPurchase?: boolean;
  canDeletePurchase?: boolean;
  canUsePos?: boolean;
  canAccessStudio?: boolean;
  canViewSale?: boolean;
  canViewContacts?: boolean;
  canCreateContact?: boolean;
  canDeleteContact?: boolean;
}

export interface CompanySettings {
  businessName: string;
  businessAddress: string;
  businessPhone: string;
  businessEmail: string;
  taxId: string;
  currency: string;
  decimalPrecision?: number;
  logoUrl?: string;
  /** Date display format: DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD */
  dateFormat?: string;
  /** Time format: 12h or 24h */
  timeFormat?: '12h' | '24h';
  /** IANA timezone e.g. Asia/Karachi */
  timezone?: string;
  /** Business type from Create Business wizard: retail, wholesale, manufacturing, rental, mixed */
  businessType?: string;
}

export interface BranchSettings {
  id: string;
  branchCode: string;
  branchName: string;
  address: string;
  phone: string;
  city?: string;
  state?: string;
  fiscalYearStart?: string;
  fiscalYearEnd?: string;
  isActive: boolean;
  isDefault: boolean;
  cashAccount: string;
  bankAccount: string;
  posCashDrawer: string;
  cashAccountId?: string;
  bankAccountId?: string;
  posCashDrawerId?: string;
}

export interface POSSettings {
  defaultCashAccount: string;
  creditSaleAllowed: boolean;
  autoPrintReceipt: boolean;
  defaultTaxRate: number;
  invoicePrefix: string;
  negativeStockAllowed: boolean;
  allowDiscount: boolean;
  maxDiscountPercent: number;
}

export interface SalesSettings {
  partialPaymentAllowed: boolean;
  defaultPaymentMethod: 'Cash' | 'Bank' | 'Mobile Wallet';
  autoLedgerEntry: boolean;
  invoicePrefix: string;
  autoDueDays: number;
  allowCreditSale: boolean;
  requireCustomerInfo: boolean;
}

export interface PurchaseSettings {
  defaultSupplierPayableAccount: string;
  overReceiveAllowed: boolean;
  purchaseApprovalRequired: boolean;
  grnRequired: boolean;
  autoPostToInventory: boolean;
  defaultPaymentTerms: number;
}

export interface InventorySettings {
  lowStockThreshold: number;
  reorderAlertDays: number;
  negativeStockAllowed: boolean;
  valuationMethod: 'FIFO' | 'LIFO' | 'Weighted Average';
  autoReorderEnabled: boolean;
  barcodeRequired: boolean;
  enablePacking: boolean; // Global toggle: ON = boxes/pieces enabled everywhere, OFF = completely hidden
  defaultUnitId: string | null; // Default unit for new products / dropdowns when none selected
}

export interface RentalSettings {
  defaultLateFeePerDay: number;
  gracePeriodDays: number;
  advanceRequired: boolean;
  advancePercentage: number;
  securityDepositRequired: boolean;
  securityDepositAmount: number;
  damageChargeEnabled: boolean;
  autoExtendAllowed: boolean;
}

export interface AccountingSettings {
  fiscalYearStart: string; // YYYY-MM-DD
  fiscalYearEnd: string;
  lockAccountingDate?: string;
  manualJournalEnabled: boolean;
  defaultCurrency: string;
  multiCurrencyEnabled: boolean;
  /**
   * Phase-3 gate for FX gain/loss / pending FX journal meanings.
   * Default false — operational Multi Currency must never auto-enable this.
   * Internal JSON key until explicit Phase-3 approval (no Settings toggle yet).
   */
  fxSettlementAccountingEnabled: boolean;
  /** Foreign currencies for import FX document dropdown (PKR always available separately). */
  activeCurrencies: { code: string; label: string }[];
  /**
   * W3 OD-1: settings-mapped Agent FX Advance / Settlement Clearing account id.
   * Never hardcode CoA code 1230; resolve via this UUID per company.
   */
  agentFxAdvanceClearingAccountId?: string | null;
  /** W3.1 settings-mapped USD custody control asset (never hardcode 1230). */
  importFxUsdCustodyControlAccountId?: string | null;
  taxCalculationMethod: 'Inclusive' | 'Exclusive';
  defaultTaxRate: number;
}

interface SettingsContextType {
  // Loading state
  loading: boolean;
  /** True after role_permissions and user permissions have been loaded (use for RULE 5: no module render before permission load) */
  isPermissionLoaded: boolean;
  
  // Company Info
  company: CompanySettings;
  updateCompanySettings: (settings: Partial<CompanySettings>) => Promise<void>;
  
  // Branch Management
  branches: BranchSettings[];
  updateBranches: (branches: BranchSettings[]) => void;
  addBranch: (branch: BranchSettings) => void;
  
  // POS Settings
  posSettings: POSSettings;
  updatePOSSettings: (settings: Partial<POSSettings>) => Promise<void>;
  
  // Sales Settings
  salesSettings: SalesSettings;
  updateSalesSettings: (settings: Partial<SalesSettings>) => Promise<void>;
  
  // Purchase Settings
  purchaseSettings: PurchaseSettings;
  updatePurchaseSettings: (settings: Partial<PurchaseSettings>) => Promise<void>;
  
  // Inventory Settings
  inventorySettings: InventorySettings;
  updateInventorySettings: (settings: Partial<InventorySettings>, options?: { silent?: boolean }) => Promise<void>;

  // Bespoke / custom orders (business_settings table)
  businessSettings: BusinessSettings;
  updateBusinessSettings: (
    settings: Partial<{
      enableBespokeOrders: boolean;
      bespokeFormConfig: Partial<BespokeFormConfig>;
    }>,
    options?: { silent?: boolean },
  ) => Promise<void>;
  
  // Rental Settings
  rentalSettings: RentalSettings;
  updateRentalSettings: (settings: Partial<RentalSettings>) => Promise<void>;
  
  // Accounting Settings
  accountingSettings: AccountingSettings;
  updateAccountingSettings: (settings: Partial<AccountingSettings>) => Promise<void>;
  
  // Default Accounts
  defaultAccounts: DefaultAccounts;
  updateDefaultAccounts: (accounts: Partial<DefaultAccounts>) => Promise<void>;
  
  // Numbering
  numberingRules: NumberingRules;
  updateNumberingRules: (rules: Partial<NumberingRules>) => Promise<void>;
  getNextNumber: (module: keyof NumberingRules) => Promise<string>;
  
  // Permissions
  currentUser: UserPermissions;
  updatePermissions: (permissions: Partial<UserPermissions>) => Promise<void>;
  
  // Module Toggles
  modules: ModuleToggles;
  updateModules: (modules: Partial<ModuleToggles>) => Promise<void>;

  // Feature Flags (Safe Zone – e.g. studio_production_v2)
  featureFlags: Record<string, boolean>;
  updateFeatureFlag: (featureKey: string, enabled: boolean) => Promise<void>;
  
  // Refresh
  refreshSettings: () => Promise<void>;
}

// ============================================
// 🎯 CONTEXT & PROVIDER
// ============================================

export const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

// Safe fallback when context is undefined (e.g. during HMR or error recovery)
const noop = () => Promise.resolve();
const noopSync = () => {};
function getDefaultSettingsStub(): SettingsContextType {
  return {
    loading: false,
    isPermissionLoaded: true,
    company: { businessName: '', businessAddress: '', businessPhone: '', businessEmail: '', taxId: '', currency: 'PKR', decimalPrecision: 2, logoUrl: undefined, dateFormat: 'DD/MM/YYYY', timeFormat: '12h', timezone: 'Asia/Karachi', businessType: undefined },
    updateCompanySettings: noop,
    branches: [],
    updateBranches: noopSync,
    addBranch: noopSync,
    posSettings: { defaultCashAccount: '', creditSaleAllowed: true, autoPrintReceipt: false, defaultTaxRate: 0, invoicePrefix: 'POS-', negativeStockAllowed: false, allowDiscount: true, maxDiscountPercent: 100 },
    updatePOSSettings: noop,
    salesSettings: { partialPaymentAllowed: true, defaultPaymentMethod: 'Cash', autoLedgerEntry: true, invoicePrefix: 'SL-', autoDueDays: 0, allowCreditSale: true, requireCustomerInfo: false },
    updateSalesSettings: noop,
    purchaseSettings: { defaultSupplierPayableAccount: '', overReceiveAllowed: false, purchaseApprovalRequired: false, grnRequired: false, autoPostToInventory: true, defaultPaymentTerms: 0 },
    updatePurchaseSettings: noop,
    inventorySettings: { lowStockThreshold: 0, reorderAlertDays: 0, negativeStockAllowed: false, valuationMethod: 'FIFO', autoReorderEnabled: false, barcodeRequired: false, enablePacking: false, defaultUnitId: null },
    updateInventorySettings: noop,
    businessSettings: {
      enableBespokeOrders: false,
      bespokeFormConfig: { ...DEFAULT_BESPOKE_FORM_CONFIG },
      customGenericProductIds: [],
    },
    updateBusinessSettings: noop,
    rentalSettings: { defaultLateFeePerDay: 0, gracePeriodDays: 0, advanceRequired: false, advancePercentage: 0, securityDepositRequired: false, securityDepositAmount: 0, damageChargeEnabled: false, autoExtendAllowed: false },
    updateRentalSettings: noop,
    accountingSettings: { fiscalYearStart: '', fiscalYearEnd: '', manualJournalEnabled: true, defaultCurrency: 'PKR', multiCurrencyEnabled: false, fxSettlementAccountingEnabled: false, activeCurrencies: [{ code: 'CNY', label: 'RMB (CNY)' }, { code: 'USD', label: 'US Dollar' }], agentFxAdvanceClearingAccountId: null, importFxUsdCustodyControlAccountId: null, taxCalculationMethod: 'Inclusive', defaultTaxRate: 0 },
    updateAccountingSettings: noop,
    defaultAccounts: { paymentMethods: [] },
    updateDefaultAccounts: noop,
    numberingRules: { salePrefix: 'SL-', saleNextNumber: 1, purchasePrefix: 'PUR-', purchaseNextNumber: 1, rentalPrefix: 'RNT-', rentalNextNumber: 1, expensePrefix: 'EXP-', expenseNextNumber: 1, productPrefix: 'PRD-', productNextNumber: 1, studioPrefix: 'STD-', studioNextNumber: 1, posPrefix: 'POS-', posNextNumber: 1, paymentPrefix: 'PAY-', paymentNextNumber: 1, jobPrefix: 'JOB-', jobNextNumber: 1, journalPrefix: 'JV-', journalNextNumber: 1 },
    updateNumberingRules: noopSync,
    getNextNumber: async () => '',
    currentUser: { role: 'Admin', canCreateSale: true, canEditSale: true, canDeleteSale: true, canCancelSale: true, canViewSale: true, canViewContacts: true, canViewReports: true, canManageSettings: true, canManageUsers: true, canAccessAccounting: true, canMakePayments: true, canReceivePayments: true, canManageExpenses: true, canManageProducts: true, canManagePurchases: true, canManageRentals: true },
    updatePermissions: noop,
    modules: {
      ...defaultModuleToggles(),
      rentalModuleEnabled: true,
      studioModuleEnabled: true,
      accountingModuleEnabled: true,
      productionModuleEnabled: true,
      posModuleEnabled: true,
    },
    updateModules: noop,
    featureFlags: {},
    updateFeatureFlag: noop,
    refreshSettings: noop,
  };
}

export const useSettings = () => {
  const context = useContext(SettingsContext);
  return context ?? getDefaultSettingsStub();
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const { companyId, branchId, user, userRole, homeCompanyId } = useSupabase();
  const [loading, setLoading] = useState<boolean>(true);

  // Company Settings
  const [company, setCompany] = useState<CompanySettings>({
    businessName: '',
    businessAddress: '',
    businessPhone: '',
    businessEmail: '',
    taxId: '',
    currency: 'PKR',
    decimalPrecision: 2,
    logoUrl: undefined,
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '12h',
    timezone: 'Asia/Karachi',
    businessType: undefined,
  });

  // Branch Management
  const [branches, setBranches] = useState<BranchSettings[]>([]);

  // POS Settings
  const [posSettings, setPOSSettings] = useState<POSSettings>({
    defaultCashAccount: '',
    creditSaleAllowed: false,
    autoPrintReceipt: false,
    defaultTaxRate: 0,
    invoicePrefix: 'POS-',
    negativeStockAllowed: false,
    allowDiscount: false,
    maxDiscountPercent: 0,
  });

  // Sales Settings
  const [salesSettings, setSalesSettings] = useState<SalesSettings>({
    partialPaymentAllowed: false,
    defaultPaymentMethod: 'Cash',
    autoLedgerEntry: false,
    invoicePrefix: 'SL-',
    autoDueDays: 0,
    allowCreditSale: false,
    requireCustomerInfo: false,
  });

  // Purchase Settings
  const [purchaseSettings, setPurchaseSettings] = useState<PurchaseSettings>({
    defaultSupplierPayableAccount: '',
    overReceiveAllowed: false,
    purchaseApprovalRequired: false,
    grnRequired: false,
    autoPostToInventory: false,
    defaultPaymentTerms: 0,
  });

  // Inventory Settings
  const [inventorySettings, setInventorySettings] = useState<InventorySettings>({
    lowStockThreshold: 0,
    reorderAlertDays: 0,
    negativeStockAllowed: false,
    valuationMethod: 'FIFO',
    autoReorderEnabled: false,
    barcodeRequired: false,
    enablePacking: false, // Default: Packing disabled
    defaultUnitId: null,
  });

  const [businessSettings, setBusinessSettings] = useState<BusinessSettings>({
    enableBespokeOrders: false,
    customGenericProductIds: [],
    bespokeFormConfig: { ...DEFAULT_BESPOKE_FORM_CONFIG },
  });

  // Rental Settings
  const [rentalSettings, setRentalSettings] = useState<RentalSettings>({
    defaultLateFeePerDay: 0,
    gracePeriodDays: 0,
    advanceRequired: false,
    advancePercentage: 0,
    securityDepositRequired: false,
    securityDepositAmount: 0,
    damageChargeEnabled: false,
    autoExtendAllowed: false,
  });

  // Accounting Settings
  const [accountingSettings, setAccountingSettings] = useState<AccountingSettings>({
    fiscalYearStart: new Date().toISOString().split('T')[0],
    fiscalYearEnd: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
    lockAccountingDate: undefined,
    manualJournalEnabled: false,
    defaultCurrency: 'PKR',
    multiCurrencyEnabled: false,
    fxSettlementAccountingEnabled: false,
    agentFxAdvanceClearingAccountId: null,
    importFxUsdCustodyControlAccountId: null,
    activeCurrencies: [
      { code: 'CNY', label: 'RMB (CNY)' },
      { code: 'USD', label: 'US Dollar' },
    ],
    taxCalculationMethod: 'Inclusive',
    defaultTaxRate: 0,
  });

  // Default Accounts
  const [defaultAccounts, setDefaultAccounts] = useState<DefaultAccounts>({
    paymentMethods: [
      { id: '1', method: 'Cash', enabled: true, defaultAccount: '' },
      { id: '2', method: 'Bank', enabled: true, defaultAccount: '' },
      { id: '3', method: 'Mobile Wallet', enabled: true, defaultAccount: '' },
    ],
  });

  // Numbering Rules (LOCKED: SL, STD, PUR, EXP, PAY, JOB, JV – separate counters)
  const [numberingRules, setNumberingRules] = useState<NumberingRules>({
    salePrefix: 'SL-',
    saleNextNumber: 1,
    quotationPrefix: 'QT-',
    quotationNextNumber: 1,
    draftNextNumber: 1,
    orderNextNumber: 1,
    purchasePrefix: 'PUR-',
    purchaseNextNumber: 1,
    rentalPrefix: 'RNT-',
    rentalNextNumber: 1,
    expensePrefix: 'EXP-',
    expenseNextNumber: 1,
    productPrefix: 'PRD-',
    productNextNumber: 1,
    studioPrefix: 'STD-',
    studioNextNumber: 1,
    posPrefix: 'POS-',
    posNextNumber: 1,
    productionPrefix: 'PRD-',
    productionNextNumber: 1,
    paymentPrefix: 'PAY-',
    paymentNextNumber: 1,
    jobPrefix: 'JOB-',
    jobNextNumber: 1,
    journalPrefix: 'JV-',
    journalNextNumber: 1,
  });

  // User Permissions
  const [currentUser, setCurrentUser] = useState<UserPermissions>({
    role: 'Staff',
    canCreateSale: false,
    canEditSale: false,
    canDeleteSale: false,
    canCancelSale: false,
    canViewSale: false,
    canViewContacts: false,
    canViewReports: false,
    canManageSettings: false,
    canManageUsers: false,
    canAccessAccounting: false,
    canMakePayments: false,
    canReceivePayments: false,
    canManageExpenses: false,
    canManageProducts: false,
    canManagePurchases: false,
    canManageRentals: false,
  });

  // Module Toggles
  const [modules, setModules] = useState<ModuleToggles>(defaultModuleToggles());

  // Feature Flags (Safe Zone – studio_production_v2, etc.)
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});

  /** In-flight guard: prevent overlapping loadAllSettings (avoids "Timer 'loadAllSettings' already exists"). */
  const loadAllSettingsInProgressRef = useRef(false);
  const lastLoadKeyRef = useRef<string | null>(null);
  const lastLoadAtRef = useRef(0);
  const settingsHydratedForCompanyRef = useRef<string | null>(null);

  // ============================================
  // 🎯 LOAD SETTINGS FROM DATABASE
  // ============================================

  const loadAllSettings = useCallback(async () => {
    if (loadAllSettingsInProgressRef.current) return;
    const loadKey = `${companyId ?? 'none'}:${branchId ?? 'none'}:${user?.id ?? 'none'}`;
    const now = Date.now();
    if (lastLoadKeyRef.current === loadKey && now - lastLoadAtRef.current < 5000) return;
    loadAllSettingsInProgressRef.current = true;
    lastLoadKeyRef.current = loadKey;
    lastLoadAtRef.current = now;
    if (import.meta.env?.DEV && isDebugErpEnabled()) {
      console.log('[PERM_DEBUG] loadAllSettings started; loading=true → isPermissionLoaded=false');
    }
    if (!companyId) {
      setLoading(false);
      loadAllSettingsInProgressRef.current = false;
      return;
    }

    const tierABlocking =
      settingsHydratedForCompanyRef.current !== companyId;
    if (tierABlocking) setLoading(true);
    try {
      console.time('loadAllSettings');

      const branchFilterId = branchId === 'all' ? null : branchId || null;

      // Tier A — shell critical (company, modules, flags, role/perms gate)
      // Platform ops: users.company_id stays HOME while React companyId is the switched tenant.
      // Never filter the profile role lookup by effective companyId or permissions stay Staff/empty.
      const profileCompanyIdForRole =
        isPlatformOperatorAppRole(userRole) && homeCompanyId
          ? homeCompanyId
          : companyId;
      const [
        { data: companyData },
        branchesData,
        moduleConfigs,
        flags,
        userData,
      ] = await Promise.all([
        supabase.from('companies').select('*').eq('id', companyId).single(),
        branchService.getAllBranches(companyId),
        settingsService.getAllModuleConfigs(companyId),
        featureFlagsService.getAll(companyId).catch(() => ({} as Record<string, boolean>)),
        user?.id && companyId
          ? (() => {
              let q = supabase
                .from('users')
                .select('role')
                .or(`id.eq.${user.id},auth_user_id.eq.${user.id}`)
                .limit(1);
              // Prefer home company row for platform ops; otherwise match active company if present.
              if (profileCompanyIdForRole) {
                q = q.eq('company_id', profileCompanyIdForRole);
              }
              return q.maybeSingle().then((r) => r.data);
            })()
          : Promise.resolve(null),
      ]);

      if (companyData) {
        const c = companyData as any;
        setCompany({
          businessName: companyData.name || '',
          businessAddress: companyData.address || '',
          businessPhone: companyData.phone || '',
          businessEmail: companyData.email || '',
          taxId: companyData.tax_number || '',
          currency: companyData.currency || 'PKR',
          decimalPrecision: c.decimal_precision ?? 2,
          logoUrl: companyData.logo_url || undefined,
          dateFormat: c.date_format || 'DD/MM/YYYY',
          timeFormat: (c.time_format === '24h' ? '24h' : '12h') as '12h' | '24h',
          timezone: c.timezone || 'Asia/Karachi',
          businessType: c.business_type || undefined,
        });
      }

      // Early branches (names filled in Tier B) so header selector works immediately
      setBranches(
        (branchesData || []).map((b: any) => ({
          id: b.id,
          branchCode: b.code || '',
          branchName: b.name || '',
          address: b.address || '',
          phone: b.phone || '',
          city: b.city || '',
          state: b.state || '',
          fiscalYearStart: b.fiscal_year_start ? String(b.fiscal_year_start).split('T')[0] : '',
          fiscalYearEnd: b.fiscal_year_end ? String(b.fiscal_year_end).split('T')[0] : '',
          isActive: b.is_active !== false,
          isDefault: !!b.is_default,
          cashAccount: '',
          bankAccount: '',
          posCashDrawer: '',
          cashAccountId: b.default_cash_account_id || undefined,
          bankAccountId: b.default_bank_account_id || undefined,
          posCashDrawerId: b.default_pos_drawer_account_id || undefined,
        })),
      );

      // Module toggles + flags (shell gating)
      setModules(
        buildModuleTogglesFromConfigRows(
          (moduleConfigs || []).map((m: { module_name: string; is_enabled: boolean }) => ({
            module_name: m.module_name,
            is_enabled: m.is_enabled,
          })),
        ),
      );
      setFeatureFlags(flags || {});

      // Permissions ASAP so shell unlocks before Tier B (sequences/settings blobs)
      const roleFromProfile = (userData as { role?: string } | null)?.role ?? userRole;
      if (roleFromProfile) {
        const role = mapAppRoleToUiRole(roleFromProfile);
        const engineRole: EngineRole = mapAppRoleToEngineRole(roleFromProfile);
        let canManagePurchases = role === 'Admin' || role === 'Manager';
        let canUsePos: boolean | undefined;
        let canAccessStudio: boolean | undefined;
        let canManageRentals = role === 'Admin' || role === 'Manager';
        let canCreateSale = role === 'Admin' || role === 'Manager';
        let canEditSale = role === 'Admin' || role === 'Manager';
        let canDeleteSale = role === 'Admin';
        let canViewReports = role === 'Admin' || role === 'Manager';
        let canViewSale = false;
        let canViewContacts = role === 'Admin' || role === 'Manager';
        let canCreateContact = role === 'Admin' || role === 'Manager';
        let canDeleteContact = role === 'Admin';
        let canManageSettings = role === 'Admin';
        let canManageUsers = role === 'Admin';
        let canAccessAccounting = role === 'Admin' || role === 'Manager';
        let canMakePayments = role === 'Admin' || role === 'Manager';
        let canReceivePayments = role === 'Admin' || role === 'Manager';
        let canManageExpenses = role === 'Admin' || role === 'Manager';
        let canManageProducts = role === 'Admin' || role === 'Manager';
        let canEditPurchase = role === 'Admin' || role === 'Manager';
        let canDeletePurchase = role === 'Admin';
        let derivedPerms: UserPermissions | null = null;
        try {
          const userId = user?.id ?? '';
          if (userId && companyId) {
            derivedPerms = await permissionEngine.loadPermissions(userId, companyId, engineRole, role);
          }
        } catch (err) {
          if (import.meta.env?.DEV && isDebugErpEnabled()) {
            console.log('[PERM_DEBUG] permissionEngine.loadPermissions threw:', err);
          }
        }
        // Platform developer/super_admin map to Admin — Admin bypass in checkPermission = full module access.
        // If role_permissions RPC fails, keep Admin defaults so company switch still unlocks the shell.
        if (derivedPerms) {
          canManagePurchases = derivedPerms.canManagePurchases;
          canUsePos = derivedPerms.canUsePos ?? (role === 'Admin' || role === 'Manager');
          canAccessStudio = derivedPerms.canAccessStudio ?? (role === 'Admin' || role === 'Manager');
          canManageRentals = derivedPerms.canManageRentals;
          canViewSale = derivedPerms.canViewSale;
          canViewReports = derivedPerms.canViewReports;
          canAccessAccounting = derivedPerms.canAccessAccounting;
          canManageProducts = derivedPerms.canManageProducts;
          canViewContacts = derivedPerms.canViewContacts;
          canCreateContact = derivedPerms.canCreateContact ?? derivedPerms.canViewContacts;
          canDeleteContact = derivedPerms.canDeleteContact ?? false;
          canManageUsers = derivedPerms.canManageUsers;
          canManageSettings = derivedPerms.canManageSettings;
          canReceivePayments = derivedPerms.canReceivePayments;
          canMakePayments = derivedPerms.canMakePayments ?? derivedPerms.canReceivePayments;
          canManageExpenses = derivedPerms.canReceivePayments;
          canEditPurchase = derivedPerms.canEditPurchase ?? derivedPerms.canManagePurchases;
          canDeletePurchase = derivedPerms.canDeletePurchase ?? false;
          canCreateSale = derivedPerms.canCreateSale;
          canEditSale = derivedPerms.canEditSale;
          canDeleteSale = derivedPerms.canDeleteSale;
        } else if (role === 'Admin') {
          canViewSale = true;
          canUsePos = true;
          canAccessStudio = true;
        }
        setCurrentUser({
          role,
          canCreateSale,
          canEditSale,
          canDeleteSale,
          canCancelSale: role === 'Admin' || role === 'Manager',
          canViewSale: canViewSale || role === 'Admin',
          canViewReports,
          canViewContacts,
          canCreateContact,
          canDeleteContact,
          canManageSettings,
          canManageUsers,
          canAccessAccounting,
          canMakePayments,
          canReceivePayments,
          canManageExpenses,
          canManageProducts,
          canManagePurchases,
          canManageRentals,
          canEditPurchase,
          canDeletePurchase,
          canUsePos: canUsePos ?? (role === 'Admin' || role === 'Manager'),
          canAccessStudio: canAccessStudio ?? (role === 'Admin' || role === 'Manager'),
        });
      }

      // Unlock shell — Tier B (settings blobs / sequences) loads after
      setLoading(false);
      if (companyId) settingsHydratedForCompanyRef.current = companyId;
      if (import.meta.env?.DEV) console.timeLog?.('loadAllSettings', 'tierA-done');

      // Tier B — settings pages / numbering (non-blocking for first paint)
      const [allSettings, businessSettingsData, erpSequences, sequences] = await Promise.all([
        settingsService.getAllSettings(companyId),
        businessSettingsService.getBusinessSettings(companyId).catch(() => ({
          enableBespokeOrders: false,
          bespokeFormConfig: { ...DEFAULT_BESPOKE_FORM_CONFIG },
          customGenericProductIds: [],
        })),
        settingsService.getErpDocumentSequences(companyId, branchFilterId).catch(() => [] as any[]),
        settingsService.getAllDocumentSequences(companyId, branchFilterId ?? undefined),
      ]);

      // Slim account name lookup for branch defaults only (AccountingContext owns full COA).
      const branchAccountIds = [
        ...new Set(
          (branchesData || []).flatMap((b: any) =>
            [b.default_cash_account_id, b.default_bank_account_id, b.default_pos_drawer_account_id].filter(
              Boolean,
            ) as string[],
          ),
        ),
      ];
      let accountsList: { id: string; name: string }[] = [];
      if (branchAccountIds.length > 0) {
        const { data: slimAccounts } = await supabase
          .from('accounts')
          .select('id, name')
          .in('id', branchAccountIds);
        accountsList = (slimAccounts as { id: string; name: string }[]) || [];
      }

      const settingsMapEarly = new Map((allSettings || []).map((s: any) => [s.key, s.value]));
      const enablePacking = settingsMapEarly.get('enable_packing') === true;

      // Resolve branch account names
      const accountNameById = new Map<string, string>();
      (accountsList || []).forEach((a: any) => { if (a.id && a.name) accountNameById.set(a.id, a.name); });
      const convertedBranches: BranchSettings[] = (branchesData || []).map((b: any) => ({
        id: b.id,
        branchCode: b.code || '',
        branchName: b.name || '',
        address: b.address || '',
        phone: b.phone || '',
        city: b.city || '',
        state: b.state || '',
        fiscalYearStart: b.fiscal_year_start ? String(b.fiscal_year_start).split('T')[0] : '',
        fiscalYearEnd: b.fiscal_year_end ? String(b.fiscal_year_end).split('T')[0] : '',
        isActive: b.is_active !== false,
        isDefault: !!b.is_default,
        cashAccount: (b.default_cash_account_id && accountNameById.get(b.default_cash_account_id)) || '',
        bankAccount: (b.default_bank_account_id && accountNameById.get(b.default_bank_account_id)) || '',
        posCashDrawer: (b.default_pos_drawer_account_id && accountNameById.get(b.default_pos_drawer_account_id)) || '',
        cashAccountId: b.default_cash_account_id || undefined,
        bankAccountId: b.default_bank_account_id || undefined,
        posCashDrawerId: b.default_pos_drawer_account_id || undefined,
      }));
      setBranches(convertedBranches);

      const settingsMap = new Map(allSettings.map(s => [s.key, s.value]));

      // Load POS Settings
      const posData = (settingsMap.get('pos_settings') as any) || {};
      setPOSSettings({
        defaultCashAccount: posData.defaultCashAccount || '',
        creditSaleAllowed: posData.creditSaleAllowed || false,
        autoPrintReceipt: posData.autoPrintReceipt || false,
        defaultTaxRate: posData.defaultTaxRate || 0,
        invoicePrefix: posData.invoicePrefix || 'POS-',
        negativeStockAllowed: posData.negativeStockAllowed || false,
        allowDiscount: posData.allowDiscount || false,
        maxDiscountPercent: posData.maxDiscountPercent || 0,
      });

      // Load Sales Settings
      const salesData = (settingsMap.get('sales_settings') as any) || {};
      setSalesSettings({
        partialPaymentAllowed: salesData.partialPaymentAllowed || false,
        defaultPaymentMethod: salesData.defaultPaymentMethod || 'Cash',
        autoLedgerEntry: salesData.autoLedgerEntry || false,
        invoicePrefix: salesData.invoicePrefix || 'SL-',
        autoDueDays: salesData.autoDueDays || 0,
        allowCreditSale: salesData.allowCreditSale || false,
        requireCustomerInfo: salesData.requireCustomerInfo || false,
      });

      // Load Purchase Settings
      const purchaseData = (settingsMap.get('purchase_settings') as any) || {};
      setPurchaseSettings({
        defaultSupplierPayableAccount: purchaseData.defaultSupplierPayableAccount || '',
        overReceiveAllowed: purchaseData.overReceiveAllowed || false,
        purchaseApprovalRequired: purchaseData.purchaseApprovalRequired || false,
        grnRequired: purchaseData.grnRequired || false,
        autoPostToInventory: purchaseData.autoPostToInventory || false,
        defaultPaymentTerms: purchaseData.defaultPaymentTerms || 0,
      });

      setBusinessSettings(businessSettingsData);

      // Load Inventory Settings (enablePacking already fetched in parallel above)
      const inventoryData = (settingsMap.get('inventory_settings') as any) || {};
      setInventorySettings({
        lowStockThreshold: inventoryData.lowStockThreshold || 0,
        reorderAlertDays: inventoryData.reorderAlertDays || 0,
        negativeStockAllowed: inventoryData.negativeStockAllowed || false,
        valuationMethod: inventoryData.valuationMethod || 'FIFO',
        autoReorderEnabled: inventoryData.autoReorderEnabled || false,
        barcodeRequired: inventoryData.barcodeRequired || false,
        enablePacking: enablePacking || false,
        defaultUnitId: inventoryData.defaultUnitId ?? null,
      });

      // Load Rental Settings
      const rentalData = (settingsMap.get('rental_settings') as any) || {};
      setRentalSettings({
        defaultLateFeePerDay: rentalData.defaultLateFeePerDay || 0,
        gracePeriodDays: rentalData.gracePeriodDays || 0,
        advanceRequired: rentalData.advanceRequired || false,
        advancePercentage: rentalData.advancePercentage || 0,
        securityDepositRequired: rentalData.securityDepositRequired || false,
        securityDepositAmount: rentalData.securityDepositAmount || 0,
        damageChargeEnabled: rentalData.damageChargeEnabled || false,
        autoExtendAllowed: rentalData.autoExtendAllowed || false,
      });

      // Load Accounting Settings
      const accountingData = (settingsMap.get('accounting_settings') as any) || {};
      setAccountingSettings({
        fiscalYearStart: accountingData.fiscalYearStart || new Date().toISOString().split('T')[0],
        fiscalYearEnd: accountingData.fiscalYearEnd || new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        lockAccountingDate: accountingData.lockAccountingDate || undefined,
        manualJournalEnabled: accountingData.manualJournalEnabled || false,
        defaultCurrency: accountingData.defaultCurrency || 'PKR',
        multiCurrencyEnabled: accountingData.multiCurrencyEnabled || false,
        // Never coerce missing/undefined to true — Phase-3 journal meanings stay off until explicit approval.
        fxSettlementAccountingEnabled: accountingData.fxSettlementAccountingEnabled === true,
        agentFxAdvanceClearingAccountId: accountingData.agentFxAdvanceClearingAccountId || null,
        importFxUsdCustodyControlAccountId: accountingData.importFxUsdCustodyControlAccountId || null,
        activeCurrencies: Array.isArray(accountingData.activeCurrencies) && accountingData.activeCurrencies.length > 0
          ? accountingData.activeCurrencies
          : [
              { code: 'CNY', label: 'RMB (CNY)' },
              { code: 'USD', label: 'US Dollar' },
            ],
        taxCalculationMethod: accountingData.taxCalculationMethod || 'Inclusive',
        defaultTaxRate: accountingData.defaultTaxRate || 0,
      });

      // Load Default Accounts
      const accountsData = (settingsMap.get('default_accounts') as any) || {};
      setDefaultAccounts({
        paymentMethods: accountsData.paymentMethods || [
          { id: '1', method: 'Cash', enabled: true, defaultAccount: '' },
          { id: '2', method: 'Bank', enabled: true, defaultAccount: '' },
          { id: '3', method: 'Mobile Wallet', enabled: true, defaultAccount: '' },
        ],
      });

      // Load Numbering Rules (erpSequences and sequences already fetched in parallel above)
      const erpMap = new Map(erpSequences.map((s: any) => [s.document_type.toLowerCase(), s]));
      const sequencesMap = new Map(sequences.map((s: any) => [s.document_type, s]));

      const getNext = (type: string) => {
        const erp = erpMap.get(type);
        if (erp) return { prefix: erp.prefix, next: erp.last_number + 1 };
        const leg = sequencesMap.get(type);
        return { prefix: leg?.prefix || '', next: leg?.current_number ?? 1 };
      };
      let studioNext = getNext('studio').next;
      if (studioNext === 1 && !erpMap.has('studio') && !sequencesMap.get('studio')) {
        try {
          const { saleService } = await import('@/app/services/saleService');
          studioNext = await saleService.getNextStudioInvoiceNumber(companyId);
        } catch {
          studioNext = 1;
        }
      }

      setNumberingRules({
        salePrefix: getNext('sale').prefix || 'SL-',
        saleNextNumber: getNext('sale').next,
        purchasePrefix: getNext('purchase').prefix || 'PUR-',
        purchaseNextNumber: getNext('purchase').next,
        rentalPrefix: getNext('rental').prefix || 'RNT-',
        rentalNextNumber: getNext('rental').next,
        expensePrefix: getNext('expense').prefix || 'EXP-',
        expenseNextNumber: getNext('expense').next,
        productPrefix: getNext('product').prefix || 'PRD-',
        productNextNumber: getNext('product').next,
        studioPrefix: getNext('studio').prefix || 'STD-',
        studioNextNumber: studioNext,
        posPrefix: getNext('pos').prefix || 'POS-',
        posNextNumber: getNext('pos').next,
        paymentPrefix: getNext('payment').prefix || 'PAY-',
        paymentNextNumber: getNext('payment').next ?? 1,
        jobPrefix: getNext('job').prefix || 'JOB-',
        jobNextNumber: getNext('job').next ?? 1,
        journalPrefix: getNext('journal').prefix || 'JV-',
        journalNextNumber: getNext('journal').next ?? 1,
      });

      // Modules/flags/permissions already applied in Tier A

      console.timeEnd('loadAllSettings');
      if (import.meta.env?.DEV && isDebugErpEnabled()) console.log('✅ Settings loaded');
    } catch (error) {
      console.error('[SETTINGS CONTEXT] Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
      if (companyId) settingsHydratedForCompanyRef.current = companyId;
      loadAllSettingsInProgressRef.current = false;
      if (import.meta.env?.DEV && isDebugErpEnabled()) {
        console.log('[PERM_DEBUG] loadAllSettings finished; loading=false → isPermissionLoaded=true');
      }
    }
  }, [companyId, branchId, user?.id, userRole, homeCompanyId]);

  // Load settings on mount
  useEffect(() => {
    loadAllSettings();
  }, [loadAllSettings]);

  // ============================================
  // 🎯 UPDATE FUNCTIONS (SAVE TO DATABASE)
  // ============================================

  const updateCompanySettings = async (settings: Partial<CompanySettings>) => {
    if (!companyId) return;
    
    setCompany(prev => ({ ...prev, ...settings }));
    
    try {
      // Update companies table
      const updatePayload: Record<string, unknown> = {
        name: settings.businessName,
        address: settings.businessAddress,
        phone: settings.businessPhone,
        email: settings.businessEmail,
        tax_number: settings.taxId,
        currency: settings.currency,
        logo_url: settings.logoUrl,
      };
      if (settings.timezone !== undefined) updatePayload.timezone = settings.timezone;
      if (settings.dateFormat !== undefined) updatePayload.date_format = settings.dateFormat;
      if (settings.timeFormat !== undefined) updatePayload.time_format = settings.timeFormat;
      await supabase
        .from('companies')
        .update(updatePayload)
        .eq('id', companyId);
      
      toast.success('Company settings saved');
    } catch (error) {
      console.error('[SETTINGS] Error saving company settings:', error);
      toast.error('Failed to save company settings');
    }
  };

  const updateBranches = (branches: BranchSettings[]) => {
    setBranches(branches);
  };

  const addBranch = (branch: BranchSettings) => {
    setBranches(prev => [...prev, branch]);
  };

  const updatePOSSettings = async (settings: Partial<POSSettings>) => {
    if (!companyId) return;
    
    const updated = { ...posSettings, ...settings };
    setPOSSettings(updated);
    
    try {
      await settingsService.setSetting(companyId, 'pos_settings', updated, 'pos', 'POS module settings');
      toast.success('POS settings saved');
    } catch (error) {
      console.error('[SETTINGS] Error saving POS settings:', error);
      toast.error('Failed to save POS settings');
    }
  };

  const updateSalesSettings = async (settings: Partial<SalesSettings>) => {
    if (!companyId) return;
    
    const updated = { ...salesSettings, ...settings };
    setSalesSettings(updated);
    
    try {
      await settingsService.setSetting(companyId, 'sales_settings', updated, 'sales', 'Sales module settings');
      toast.success('Sales settings saved');
    } catch (error) {
      console.error('[SETTINGS] Error saving sales settings:', error);
      toast.error('Failed to save sales settings');
    }
  };

  const updatePurchaseSettings = async (settings: Partial<PurchaseSettings>) => {
    if (!companyId) return;
    
    const updated = { ...purchaseSettings, ...settings };
    setPurchaseSettings(updated);
    
    try {
      await settingsService.setSetting(companyId, 'purchase_settings', updated, 'purchase', 'Purchase module settings');
      toast.success('Purchase settings saved');
    } catch (error) {
      console.error('[SETTINGS] Error saving purchase settings:', error);
      toast.error('Failed to save purchase settings');
    }
  };

  const updateInventorySettings = async (settings: Partial<InventorySettings>, options?: { silent?: boolean }) => {
    if (!companyId) return;
    
    const updated = { ...inventorySettings, ...settings };
    setInventorySettings(updated);
    
    try {
      // Company-level: save to DB (ensure boolean, not string) so all users get same behavior
      const { enablePacking, ...otherSettings } = updated;
      const payload = { ...otherSettings, negativeStockAllowed: Boolean(otherSettings.negativeStockAllowed) };
      await settingsService.setSetting(companyId, 'inventory_settings', payload, 'inventory', 'Inventory module settings');
      settingsService.clearAllowNegativeStockCache(companyId);

      if (settings.enablePacking !== undefined) {
        await settingsService.setEnablePacking(companyId, settings.enablePacking);
      }
      
      // Refetch from DB so context (and Sales/POS) see latest; avoids stale cache after toggle
      await loadAllSettings();
      if (!options?.silent) toast.success('Inventory settings saved');
    } catch (error) {
      console.error('[SETTINGS] Error saving inventory settings:', error);
      toast.error('Failed to save inventory settings');
    }
  };

  const updateBusinessSettings = async (
    settings: Partial<{
      enableBespokeOrders: boolean;
      bespokeFormConfig: Partial<import('@/app/types/bespoke').BespokeFormConfig>;
    }>,
    options?: { silent?: boolean },
  ) => {
    if (!companyId) return;
    const updated = await businessSettingsService.updateBusinessSettings(companyId, {
      enableBespokeOrders: settings.enableBespokeOrders,
      bespokeFormConfig: settings.bespokeFormConfig,
    });
    setBusinessSettings(updated);
    if (!options?.silent) toast.success('Bespoke order settings saved');
  };

  const updateRentalSettings = async (settings: Partial<RentalSettings>) => {
    if (!companyId) return;
    
    const updated = { ...rentalSettings, ...settings };
    setRentalSettings(updated);
    
    try {
      await settingsService.setSetting(companyId, 'rental_settings', updated, 'rental', 'Rental module settings');
      toast.success('Rental settings saved');
    } catch (error) {
      console.error('[SETTINGS] Error saving rental settings:', error);
      toast.error('Failed to save rental settings');
    }
  };

  const updateAccountingSettings = async (settings: Partial<AccountingSettings>) => {
    if (!companyId) return;

    const previous = accountingSettings;
    const turningOff =
      previous.multiCurrencyEnabled === true && settings.multiCurrencyEnabled === false;

    if (turningOff) {
      const { assertCanDisableImportFx, formatImportFxServerError } = await import(
        '@/app/lib/importFxServerGate'
      );
      const gate = await assertCanDisableImportFx(companyId);
      if (!gate.ok) {
        toast.error(formatImportFxServerError(gate.error || gate));
        return;
      }
    }

    const updated: AccountingSettings = {
      ...accountingSettings,
      ...settings,
      // Phase-3 gate: never leave undefined; never promote Multi Currency ON into FX P&L.
      fxSettlementAccountingEnabled:
        (settings.fxSettlementAccountingEnabled ?? accountingSettings.fxSettlementAccountingEnabled) === true,
    };
    setAccountingSettings(updated);

    try {
      await settingsService.setSetting(companyId, 'accounting_settings', updated, 'accounting', 'Accounting module settings');
      if (updated.fiscalYearStart) {
        await supabase
          .from('companies')
          .update({ financial_year_start: updated.fiscalYearStart })
          .eq('id', companyId);
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(FISCAL_YEAR_CONFIG_UPDATED_EVENT));
      }
      toast.success('Accounting settings saved');
    } catch (error) {
      console.error('[SETTINGS] Error saving accounting settings:', error);
      setAccountingSettings(previous);
      const { formatImportFxServerError } = await import('@/app/lib/importFxServerGate');
      toast.error(formatImportFxServerError(error, 'Failed to save accounting settings'));
    }
  };

  const updateDefaultAccounts = async (accounts: Partial<DefaultAccounts>) => {
    if (!companyId) return;
    
    const updated = { ...defaultAccounts, ...accounts };
    setDefaultAccounts(updated);
    
    try {
      await settingsService.setSetting(companyId, 'default_accounts', updated, 'accounts', 'Default payment accounts');
      toast.success('Default accounts saved');
    } catch (error) {
      console.error('[SETTINGS] Error saving default accounts:', error);
      toast.error('Failed to save default accounts');
    }
  };

  const updateNumberingRules = async (rules: Partial<NumberingRules>) => {
    if (!companyId) return;

    const updated = { ...numberingRules, ...rules };
    setNumberingRules(updated);

    try {
      const documentTypeMap: Record<string, { prefix: string; nextNumber: number }> = {
        sale: { prefix: updated.salePrefix, nextNumber: updated.saleNextNumber },
        purchase: { prefix: updated.purchasePrefix, nextNumber: updated.purchaseNextNumber },
        rental: { prefix: updated.rentalPrefix, nextNumber: updated.rentalNextNumber },
        expense: { prefix: updated.expensePrefix, nextNumber: updated.expenseNextNumber },
        product: { prefix: updated.productPrefix, nextNumber: updated.productNextNumber },
        studio: { prefix: updated.studioPrefix, nextNumber: updated.studioNextNumber },
        pos: { prefix: updated.posPrefix, nextNumber: updated.posNextNumber },
        payment: { prefix: updated.paymentPrefix ?? 'PAY-', nextNumber: updated.paymentNextNumber ?? 1 },
        job: { prefix: updated.jobPrefix ?? 'JOB-', nextNumber: updated.jobNextNumber ?? 1 },
        journal: { prefix: updated.journalPrefix ?? 'JV-', nextNumber: updated.journalNextNumber ?? 1 },
      };

      const branch = branchId === 'all' ? undefined : branchId || undefined;

      for (const [docType, { prefix, nextNumber }] of Object.entries(documentTypeMap)) {
        await settingsService.setDocumentSequence(companyId, branch, docType, prefix, nextNumber, 4);
      }

      // Sync to ERP Numbering Engine so generate_document_number uses these prefixes and next number
      try {
        for (const [docType, { prefix, nextNumber }] of Object.entries(documentTypeMap)) {
          await settingsService.setErpDocumentSequence(companyId, branch ?? null, docType, prefix, Math.max(0, nextNumber - 1), 4);
        }
      } catch (erpErr) {
        // Table may not exist yet; legacy document_sequences still saved
      }

      toast.success('Numbering rules saved');
    } catch (error) {
      console.error('[SETTINGS] Error saving numbering rules:', error);
      toast.error('Failed to save numbering rules');
    }
  };

  const getNextNumber = async (module: keyof NumberingRules): Promise<string> => {
    if (!companyId) {
      // Fallback to local increment if no company
      const prefixKey = module.replace('NextNumber', 'Prefix') as keyof NumberingRules;
      const prefix = numberingRules[prefixKey] as string;
      const nextNum = numberingRules[module] as number;
      setNumberingRules(prev => ({
        ...prev,
        [module]: nextNum + 1,
      }));
      return `${prefix}${String(nextNum).padStart(4, '0')}`;
    }

    // Map module to document type
    const docTypeMap: Record<string, string> = {
      saleNextNumber: 'sale',
      purchaseNextNumber: 'purchase',
      rentalNextNumber: 'rental',
      expenseNextNumber: 'expense',
      productNextNumber: 'product',
      studioNextNumber: 'studio',
      posNextNumber: 'pos',
      paymentNextNumber: 'payment',
      jobNextNumber: 'job',
      journalNextNumber: 'journal',
    };

    const docType = docTypeMap[module];
    if (!docType) {
      throw new Error(`Unknown module: ${module}`);
    }

    try {
      const nextNumber = await settingsService.getNextDocumentNumber(companyId, branchId === 'all' ? undefined : branchId || undefined, docType);
      // Update local state
      const prefixKey = module.replace('NextNumber', 'Prefix') as keyof NumberingRules;
      const prefix = numberingRules[prefixKey] as string;
      const currentNum = parseInt(nextNumber.replace(prefix, '')) || 0;
      setNumberingRules(prev => ({
        ...prev,
        [module]: currentNum + 1,
      }));
      return nextNumber;
    } catch (error) {
      console.error('[SETTINGS] Error getting next number:', error);
      // Fallback to local increment
      const prefixKey = module.replace('NextNumber', 'Prefix') as keyof NumberingRules;
      const prefix = numberingRules[prefixKey] as string;
      const nextNum = numberingRules[module] as number;
      setNumberingRules(prev => ({
        ...prev,
        [module]: nextNum + 1,
      }));
      return `${prefix}${String(nextNum).padStart(4, '0')}`;
    }
  };

  /** @deprecated Legacy settings.user_permissions store — use role_permissions matrix instead. */
  const updatePermissions = async (permissions: Partial<UserPermissions>) => {
    if (!companyId) return;
    setCurrentUser((prev) => ({ ...prev, ...permissions }));
  };

  const updateModules = async (newModules: Partial<ModuleToggles>) => {
    if (!companyId) return;
    
    const updated = { ...modules, ...newModules };
    setModules(updated);
    
    try {
      // Save each module toggle
      const patches = moduleTogglePatchesToDb(newModules);
      for (const { moduleName, isEnabled } of patches) {
        await settingsService.setModuleEnabled(companyId, moduleName, isEnabled);
      }
      
      toast.success('Module toggles saved');
    } catch (error) {
      console.error('[SETTINGS] Error saving module toggles:', error);
      toast.error('Failed to save module toggles');
    }
  };

  const updateFeatureFlag = async (featureKey: string, enabled: boolean) => {
    if (!companyId) return;
    const previous = featureFlags[featureKey];
    setFeatureFlags((prev) => ({ ...prev, [featureKey]: enabled }));
    try {
      await featureFlagsService.setEnabled(companyId, featureKey, enabled);
      toast.success('Feature setting saved');
    } catch (error) {
      console.error('[SETTINGS] Error saving feature flag:', error);
      setFeatureFlags((prev) => ({ ...prev, [featureKey]: previous }));
      toast.error('Failed to save feature setting. Run migration feature_flags_table.sql if needed.');
    }
  };

  const value = useMemo<SettingsContextType>(() => ({
    loading,
    isPermissionLoaded: !loading,
    company,
    updateCompanySettings,
    branches,
    updateBranches,
    addBranch,
    posSettings,
    updatePOSSettings,
    salesSettings,
    updateSalesSettings,
    purchaseSettings,
    updatePurchaseSettings,
    inventorySettings,
    updateInventorySettings,
    businessSettings,
    updateBusinessSettings,
    rentalSettings,
    updateRentalSettings,
    accountingSettings,
    updateAccountingSettings,
    defaultAccounts,
    updateDefaultAccounts,
    numberingRules,
    updateNumberingRules,
    getNextNumber,
    currentUser,
    updatePermissions,
    modules,
    updateModules,
    featureFlags,
    updateFeatureFlag,
    refreshSettings: loadAllSettings,
  }), [
    loading, company, branches, posSettings, salesSettings, purchaseSettings,
    inventorySettings, businessSettings, rentalSettings, accountingSettings, defaultAccounts,
    numberingRules, currentUser, modules, featureFlags,
    updateCompanySettings, updateBranches, addBranch, updatePOSSettings,
    updateSalesSettings, updatePurchaseSettings, updateInventorySettings, updateBusinessSettings,
    updateRentalSettings, updateAccountingSettings, updateDefaultAccounts,
    updateNumberingRules, getNextNumber, updatePermissions, updateModules,
    updateFeatureFlag, loadAllSettings,
  ]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};